import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';

/**
 * Input Component
 *
 * Styled input field component
 */

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /** Input size variant */
  inputSize?: 'sm' | 'md';
  /**
   * When true, renders the label visibly above the field instead of the
   * default screen-reader-only treatment. Defaults to false to preserve
   * existing behavior for all current callers.
   */
  labelVisible?: boolean;
  /** Optional action (e.g. a link) rendered right-aligned on the label row. Requires labelVisible. */
  labelAction?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, inputSize = 'md', labelVisible = false, labelAction, className = '', 'aria-describedby': callerDescribedBy, ...props }, ref) => {
    // Mobile renders at 16px (prevents iOS Safari zoom-on-focus); desktop (md+) keeps current sizing.
    const sizeClass = inputSize === 'sm' ? 'px-3 h-[34px] text-[16px] lg:h-auto lg:px-4 lg:py-3 lg:text-[12px]' : 'px-4 py-3 text-[16px] lg:text-[12px]';

    // Derive a stable error-element id from the input's id prop so
    // aria-describedby links the <input> to its error message automatically.
    const errorId = error && props.id ? `${props.id}-error` : undefined;

    // Merge any caller-supplied aria-describedby with the auto-generated errorId.
    const describedBy = [callerDescribedBy, errorId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        {label && (
          labelVisible && labelAction ? (
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <label className="block text-sm font-medium text-gray-700" htmlFor={props.id}>
                {label}
              </label>
              {labelAction}
            </div>
          ) : (
            <label
              className={labelVisible ? 'block text-sm font-medium text-gray-700 mb-1.5' : 'sr-only'}
              htmlFor={props.id}
            >
              {label}
            </label>
          )
        )}
        <input
          ref={ref}
          className={`
            w-full ${sizeClass}
            border border-gray-400 rounded-lg
            text-gray-700 placeholder-gray-400
            focus:outline-none focus:border-gray-600
            transition-colors duration-150
            disabled:bg-gray-50 disabled:cursor-not-allowed
            ${error ? 'border-danger' : ''}
            ${className}
          `}
          aria-describedby={describedBy}
          {...props}
        />
        {error && (
          <p id={errorId} className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
